// @givmo/app-charity — TanStack Query client factory.
//
// One `QueryClient` per app instance (a factory, not a module singleton, so tests get a fresh client
// and there's no cross-test cache bleed). Defaults are conservative for a portal: a short stale
// window and a single retry — the api-client already surfaces `GivmoError` with a support reference,
// so aggressive retries would only delay an actionable error. Mirrors the admin app's factory.
//
// ★ A 404 OR A 403 IS AN ANSWER, NEVER A BLIP: no retry (the KCC dev build, 2026-10-31). React Query
// holds a retry until the window has focus again, so a retried miss in a tab nobody was looking at (a
// link to a page of another organization than the one active) painted its loading state until someone
// clicked into the tab. Not found and not permitted don't change on a second ask a second later.

import { QueryClient } from '@tanstack/react-query';
import { isGivmoError } from '@givmo/api-client';

/** The console's one retry, never for a miss or a refusal. */
export function retryUnlessAnswered(failureCount: number, error: unknown): boolean {
  if (isGivmoError(error) && (error.status === 404 || error.status === 403)) return false;
  return failureCount < 1;
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        retry: retryUnlessAnswered,
        refetchOnWindowFocus: false,
      },
      mutations: {
        retry: 0,
      },
    },
  });
}
